import type { IconProps } from '@carboncopyco/icons';
import { EarthIcon, GroupAddIcon, LockIcon } from '@carboncopyco/icons';
import type { WorkspaceMember, WorkspaceNarrowType } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Form,
  InputTagsField,
  Loading,
  Modal,
  SubmitButton,
  TextButton,
  TextCaption,
  TextSubHeader,
} from '@carboncopyco/ui';
import type { FormikErrors } from 'formik';
import type React from 'react';
import { array, object, string } from 'yup';
import { WorkspaceMembersTable } from './WorkspaceMembersTable';

export type WorkspaceMembersModalForm = {
  emails: string[];
};

const createWorkspaceMembersSchema = (
  organizationName: string,
  memberEmails: string[]
) =>
  object({
    emails: array()
      .of(
        string()
          .email('Invalid email')
          .test(
            'is-org-member',
            `All invitees must be members of ${organizationName}`,
            (val) => !!(val && memberEmails.includes(val))
          )
      )
      .min(1, 'Please fill at least one email address'),
  });

const workspaceTypeToMessageMap: Record<
  WorkspaceNarrowType,
  {
    icon: React.FunctionComponent<IconProps>;
    label: string;
  }
> = {
  public: {
    icon: EarthIcon,
    label: 'Anyone can find and access this workspace',
  },
  private: {
    icon: LockIcon,
    label: 'Only invited members can find and access current workspace',
  },
};
export type WorkspaceTableColumn = {
  width: string;
  title: string;
};
export type TableColumns = Record<string, WorkspaceTableColumn>;

const workspaceMembersColumns: TableColumns = {
  member: { width: 'w-ds-input', title: 'member' },
  role: { width: '', title: 'role' },
};

export interface WorkspaceMembersModalProps {
  members: WorkspaceMember[];
  isOpen: boolean;
  inviteMembers: (emails: string[]) => void;
  handleClose: () => void;
  type: WorkspaceNarrowType;
  isSendingInvites: boolean;
  userId: string;
  removeMember: (id: string) => void;
  fetchMoreMembers: () => void;
  isLoadingMembers: boolean;
  isLoadingMoreMembers: boolean;
  organizationName: string;
  organizationMembers: string[];
  showRowDropdown?: boolean;
  handleFormErrorAnalytics?: (
    errors?: FormikErrors<WorkspaceMembersModalForm>
  ) => void;
}

export const WorkspaceMembersModal = ({
  isOpen,
  members,
  inviteMembers,
  handleClose,
  type,
  isSendingInvites,
  userId,
  removeMember,
  fetchMoreMembers,
  isLoadingMembers,
  isLoadingMoreMembers,
  organizationName,
  organizationMembers,
  showRowDropdown,
  handleFormErrorAnalytics,
}: WorkspaceMembersModalProps) => {
  const workspaceMembersSchema = createWorkspaceMembersSchema(
    organizationName,
    organizationMembers
  );

  const Icon = workspaceTypeToMessageMap[type]?.icon;

  return (
    <Modal
      containerClass="w-ds-146 min-h-ds-102"
      open={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center">
          <div className="mr-ds-2.5">
            <GroupAddIcon />
          </div>
          <TextSubHeader label="Workspace members" />
        </div>
      }
    >
      <Form<WorkspaceMembersModalForm>
        resetOnSubmit
        initialValues={{ emails: [] }}
        validationSchema={workspaceMembersSchema}
        onSubmit={(values) => {
          inviteMembers(values.emails);
        }}
        onError={handleFormErrorAnalytics}
      >
        <>
          <div className="mt-ds-4 flex items-start gap-2.5">
            <InputTagsField
              name="emails"
              initialValue={[]}
              placeholder="Type email"
              maxHeight={150}
            />
            <SubmitButton
              render={({ isValid }: { isValid: boolean }) => (
                <Button
                  disabled={isSendingInvites || !isValid}
                  submit
                >
                  <div className="w-ds-19 flex justify-center">
                    {isSendingInvites ? (
                      <Loading color="text-ds-natural-300" />
                    ) : (
                      <TextButton
                        color="text-current"
                        label="Add member"
                      />
                    )}
                  </div>
                </Button>
              )}
            />
          </div>

          <div className="mt-ds-7 flex items-center gap-1">
            {Icon && (
              <Icon
                size={16}
                color={ColorHex.natural400}
              />
            )}
            <TextCaption
              label={workspaceTypeToMessageMap[type]?.label}
              color="text-ds-natural-400"
            />
          </div>

          <div className="mt-ds-5 mb-ds-2.5">
            <WorkspaceMembersTable
              members={members}
              columns={workspaceMembersColumns}
              userId={userId}
              removeMember={removeMember}
              fetchMoreMembers={fetchMoreMembers}
              isLoadingMembers={isLoadingMembers}
              isLoadingMoreMembers={isLoadingMoreMembers}
              showRowDropdown={showRowDropdown}
            />
          </div>
        </>
      </Form>
    </Modal>
  );
};
